/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Box, MenuItem, Stack, TextField } from '@mui/material';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { PaymentGatewayType, paymentGatewayTypes } from '../../../store/modules/merchant/types';
import { pspInputForms } from '../../../store/modules/merchant/constants';
import { merchantDomainErrorMessage } from '../../../util/helpers';
import SaveBar from '../../../common/SaveBar/SaveBar';
import {
  FormSection,
  NameDomainFields,
  PaymentGatewayFields,
} from '../../../common/MerchantCreate';
import { normalizeShopifyExternalId } from '../../../util/shopifyExternalId';
import { normalizeSquarespaceExternalId } from '../../../util/squarespaceExternalId';

const ecommercePlatformOptions = ['custom', 'shopify', 'bigcommerce', 'squarespace'] as const;
export type EcommercePlatform = (typeof ecommercePlatformOptions)[number];

/**
 * What identifies a store on each platform
 */
const externalIdField: Record<
  Exclude<EcommercePlatform, 'custom'>,
  { label: string; helperText: string; placeholder: string }
> = {
  shopify: {
    label: 'Shopify Store',
    helperText: 'Paste a *.myshopify.com URL or hostname',
    placeholder: 'store.myshopify.com',
  },
  bigcommerce: {
    label: 'Store Hash',
    helperText: 'Enter the BigCommerce store hash',
    placeholder: 'abc123storehash',
  },
  squarespace: {
    label: 'Squarespace Site',
    helperText: 'Paste the built-in *.squarespace.com address, or just the subdomain',
    placeholder: 'wombat-gopher-t4x8',
  },
};

export const merchantIndustryValues = [
  'Bill_Pay',
  'Restaurant',
  'Retail',
  'Transportation',
  'Sporting_Goods',
] as const;

export type MerchantIndustryValue = (typeof merchantIndustryValues)[number];

export type CreateMerchantFormValues = {
  name: string;
  industry: MerchantIndustryValue | 'other' | '';
  industryOther: string;
  supportEmail: string;
  domain: string;
  ecommercePlatform: EcommercePlatform;
  externalId: string;
  paymentGateway: PaymentGatewayType | 'none';
  paymentGatewayData?: Record<string, string>;
};

export const resolveCreateMerchantIndustry = (formValues: CreateMerchantFormValues): string =>
  formValues.industry === 'other' ? formValues.industryOther.trim() : formValues.industry;

const createMerchantValidationSchema = z
  .object({
    name: z.string().min(1, 'Name is required'),
    industry: z.enum([...merchantIndustryValues, 'other'], {
      errorMap: () => ({ message: 'Industry is required' }),
    }),
    industryOther: z.string(),
    supportEmail: z.union([z.literal(''), z.string().email('Enter a valid email')]),
    domain: z.string().refine(
      (value) => !value || merchantDomainErrorMessage(value) === '',
      (value) => ({ message: merchantDomainErrorMessage(value) }),
    ),
    ecommercePlatform: z.enum(ecommercePlatformOptions),
    externalId: z.string(),
    paymentGateway: z.enum([...paymentGatewayTypes, 'none']),
    paymentGatewayData: z.record(z.string(), z.string().min(1)).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.industry === 'other' && !data.industryOther.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter an industry',
        path: ['industryOther'],
      });
    }
  })
  .superRefine((data, ctx) => {
    if (data.ecommercePlatform === 'custom') {
      return;
    }

    if (!data.externalId.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'External ID is required',
        path: ['externalId'],
      });
      return;
    }

    if (data.ecommercePlatform === 'shopify') {
      const normalized = normalizeShopifyExternalId(data.externalId);
      if (!normalized) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter a valid *.myshopify.com store hostname or URL',
          path: ['externalId'],
        });
      }
    }

    if (data.ecommercePlatform === 'squarespace') {
      const normalized = normalizeSquarespaceExternalId(data.externalId);
      if (!normalized) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Enter the built-in *.squarespace.com address or subdomain, not a custom domain',
          path: ['externalId'],
        });
      }
    }
  })
  .superRefine((data, ctx) => {
    if (data.paymentGateway === 'none') {
      return;
    }

    const fields = pspInputForms[data.paymentGateway]?.fields ?? [];
    fields.forEach((field) => {
      if (!data.paymentGatewayData?.[field.name]?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${field.label} is required`,
          path: ['paymentGatewayData', field.name],
        });
      }
    });
  });

type Props = {
  onSubmit: (data: CreateMerchantFormValues) => void | Promise<void>;
  isSubmitting: boolean;
  readOnly?: boolean;
};

export const CreateMerchantForm = ({ onSubmit, isSubmitting, readOnly = false }: Props) => {
  const methods = useForm<CreateMerchantFormValues>({
    resolver: zodResolver(createMerchantValidationSchema),
    defaultValues: {
      name: '',
      industry: '',
      industryOther: '',
      supportEmail: '',
      domain: '',
      ecommercePlatform: 'custom',
      externalId: '',
      paymentGateway: 'none',
      paymentGatewayData: {},
    },
  });

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    reset,
    formState: { errors, dirtyFields },
  } = methods;

  const ecommercePlatform = watch('ecommercePlatform');
  const industry = watch('industry');

  const submitAndReset = async (data: CreateMerchantFormValues) => {
    try {
      await onSubmit(data);
      reset(undefined, { keepDirty: false, keepDirtyValues: false });
    } catch {
      // Keep form values; onSubmit already toasted.
    }
  };

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        id="create-merchant-form"
        onSubmit={handleSubmit(submitAndReset)}
        sx={{ pb: 6 }}
      >
        <Stack spacing={4}>
          <FormSection title="Account Details">
            <NameDomainFields nameLabel="Merchant Name" readOnly={readOnly}>
              <Controller
                control={control}
                name="industry"
                render={({ field }) => (
                  <TextField
                    label="Industry"
                    select
                    required
                    {...field}
                    disabled={readOnly}
                    error={!!errors.industry}
                    helperText={errors.industry?.message}
                    onChange={(e) => {
                      field.onChange(e);
                      if (e.target.value !== 'other') {
                        setValue('industryOther', '');
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select an industry
                    </MenuItem>
                    {merchantIndustryValues.map((value) => (
                      <MenuItem key={value} value={value}>
                        {value}
                      </MenuItem>
                    ))}
                    <MenuItem value="other">Other (enter below)</MenuItem>
                  </TextField>
                )}
              />
              {industry === 'other' && (
                <TextField
                  label="Other industry"
                  {...register('industryOther')}
                  required
                  disabled={readOnly}
                  error={!!errors.industryOther}
                  helperText={errors.industryOther?.message}
                />
              )}
              <TextField
                label="Support Email"
                {...register('supportEmail')}
                disabled={readOnly}
                error={!!errors.supportEmail}
                helperText={errors.supportEmail?.message}
              />
            </NameDomainFields>
          </FormSection>
          <FormSection title="Ecommerce Platform">
            <Controller
              control={control}
              name="ecommercePlatform"
              render={({ field }) => (
                <TextField
                  label="Platform"
                  select
                  {...field}
                  disabled={readOnly}
                  onChange={(e) => {
                    field.onChange(e);
                    setValue('externalId', '');
                  }}
                >
                  <MenuItem value="custom">Custom</MenuItem>
                  <MenuItem value="shopify">Shopify</MenuItem>
                  <MenuItem value="bigcommerce">BigCommerce</MenuItem>
                  <MenuItem value="squarespace">Squarespace</MenuItem>
                </TextField>
              )}
            />
            {ecommercePlatform !== 'custom' && (
              <TextField
                label={externalIdField[ecommercePlatform].label}
                {...register('externalId')}
                required
                disabled={readOnly}
                error={!!errors.externalId}
                helperText={
                  errors.externalId?.message ?? externalIdField[ecommercePlatform].helperText
                }
                placeholder={externalIdField[ecommercePlatform].placeholder}
              />
            )}
          </FormSection>
          <PaymentGatewayFields readOnly={readOnly} />
        </Stack>
      </Box>
      <SaveBar
        text={isSubmitting ? 'Creating merchant...' : 'Create merchant?'}
        show={Object.keys(dirtyFields).length > 0 && !readOnly}
        disableSave={Object.keys(errors).length > 0 || readOnly}
        onCancel={() => {
          reset(undefined, { keepDirty: false, keepDirtyValues: false });
        }}
        formId="create-merchant-form"
        saveLoading={isSubmitting}
      />
    </FormProvider>
  );
};
